import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, AlertCircle, CheckCircle2, ExternalLink, Copy, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

/**
 * AdminSetup page — hidden, unlisted route at /admin-setup.
 *
 * Only renders the form when the backend env var BOOTSTRAP_ADMIN_TOKEN is
 * set. When the operator removes the env var after onboarding, this page
 * automatically becomes inert ("Setup mode is not enabled").
 *
 * Not linked from any navigation or marketing page. Discoverability is by
 * URL only, and even then the form does nothing without the bootstrap secret
 * the operator entered into Emergent's deploy settings.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminSetup = () => {
  const [statusLoading, setStatusLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [allowlist, setAllowlist] = useState([]);
  const [email, setEmail] = useState('');
  const [bootstrapToken, setBootstrapToken] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/auth/bootstrap-status`);
        if (!cancelled) {
          setEnabled(true);
          setAllowlist(r.data?.allowlist || []);
          if (r.data?.allowlist?.length) setEmail(r.data.allowlist[0].email);
        }
      } catch (e) {
        if (!cancelled) setEnabled(false);
      } finally {
        if (!cancelled) setStatusLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setResult(null);
    if (!bootstrapToken.trim()) {
      setError('Paste the bootstrap token from your Emergent env vars.');
      return;
    }
    if (!email) {
      setError('Select an account.');
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(
        `${API}/auth/bootstrap-admin-reset`,
        { email },
        { headers: { 'X-Bootstrap-Token': bootstrapToken.trim() } }
      );
      setResult(r.data);
      toast.success(`Reset link issued for ${email}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      if (status === 404) {
        setError('Bootstrap mode is no longer enabled. The setup window has been closed.');
        setEnabled(false);
      } else if (status === 403) {
        setError(typeof detail === 'string' ? detail : 'The bootstrap token is incorrect or this email is not on the allowlist.');
      } else {
        setError(typeof detail === 'string' ? detail : 'Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const copyResetLink = () => {
    if (result?.reset_url) {
      navigator.clipboard?.writeText(result.reset_url);
      toast.success('Reset link copied');
    }
  };

  const startOver = () => {
    setResult(null);
    setError('');
    // Keep the bootstrap token entered — operator will reuse it for the next account.
  };

  // -------------------- RENDER STATES --------------------

  if (statusLoading) {
    return (
      <div className="min-h-screen bg-[#F3F5F7] flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-stone-400" />
      </div>
    );
  }

  if (!enabled) {
    // Looks like a calm 404, not a system that says "you're close to a back door".
    return (
      <div className="min-h-screen bg-[#F3F5F7] flex items-center justify-center p-4">
        <div className="fixed inset-0 grain-overlay" />
        <Card className="border-stone-200 max-w-md w-full relative z-10">
          <CardHeader>
            <CardTitle className="font-heading text-2xl text-center">Setup mode is not enabled</CardTitle>
            <CardDescription className="text-center">
              This page is inactive. If you reached it by mistake, head back to{' '}
              <Link to="/login" className="text-[#3F617E] underline">sign in</Link>.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  // ----------------- ACTIVE SETUP UI -----------------
  return (
    <div className="min-h-screen bg-[#F3F5F7] p-4 py-8">
      <div className="fixed inset-0 grain-overlay" />
      <div className="max-w-2xl mx-auto relative z-10">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-50 border border-emerald-100 mb-3">
            <ShieldCheck className="h-6 w-6 text-emerald-700" />
          </div>
          <h1 className="font-heading text-3xl text-[#3F617E] mb-1">Admin Setup</h1>
          <p className="text-stone-500 text-sm">
            One-time bootstrap window. Use this page to set passwords for your three accounts, then remove the env var.
          </p>
        </div>

        {/* Steps card */}
        <Card className="border-stone-200 shadow-sm mb-5">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-heading">How this works</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-stone-600 leading-relaxed space-y-2">
            <p><span className="font-medium text-stone-800">1.</span> Paste the bootstrap token (the long string you added to Emergent env vars as <span className="font-mono text-[12.5px]">BOOTSTRAP_ADMIN_TOKEN</span>).</p>
            <p><span className="font-medium text-stone-800">2.</span> Pick an account, click <em>Generate reset link</em>, then click <em>Open reset page</em> to set a strong password.</p>
            <p><span className="font-medium text-stone-800">3.</span> Repeat for the other two accounts.</p>
            <p><span className="font-medium text-stone-800">4.</span> Once all three are set, go back to Emergent and remove <span className="font-mono text-[12.5px]">BOOTSTRAP_ADMIN_TOKEN</span>. This page will then say &ldquo;Setup mode is not enabled&rdquo;.</p>
          </CardContent>
        </Card>

        {/* Form card */}
        <Card className="border-stone-200 shadow-md">
          <CardHeader>
            <CardTitle className="font-heading text-xl">Generate a reset link</CardTitle>
            <CardDescription>
              Reset links are single-use and expire in 1 hour. Each new link for the same account invalidates the previous one.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-4 border-red-200 bg-red-50">
                <AlertCircle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-800">{error}</AlertDescription>
              </Alert>
            )}

            {result ? (
              <div className="space-y-4" data-testid="admin-setup-result">
                <Alert className="border-emerald-200 bg-emerald-50">
                  <CheckCircle2 className="h-4 w-4 text-emerald-700" />
                  <AlertDescription className="text-emerald-900">
                    Reset link issued for <span className="font-medium">{result.email}</span>{' '}
                    ({result.role}).{' '}
                    {result.created && <span>New ADMIN account was created. </span>}
                    {result.legacy_admin_action === 'disabled' && <span>Legacy <span className="font-mono">admin@maris.local</span> has been auto-disabled.</span>}
                  </AlertDescription>
                </Alert>

                <div className="space-y-2">
                  <Label>Your one-time reset link (valid for 1 hour)</Label>
                  <div className="flex gap-2">
                    <Input value={result.reset_url} readOnly className="font-mono text-xs" />
                    <Button type="button" variant="outline" onClick={copyResetLink} title="Copy">
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    type="button"
                    className="flex-1 bg-[#3F617E] hover:bg-[#345470] rounded-full"
                    onClick={() => window.open(result.reset_url, '_blank', 'noopener,noreferrer')}
                    data-testid="open-reset-page-button"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" /> Open reset page in new tab
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-full"
                    onClick={startOver}
                    data-testid="setup-next-account-button"
                  >
                    Set up another account
                  </Button>
                </div>

                <p className="text-[11.5px] text-stone-500 leading-relaxed pt-2 border-t border-stone-100">
                  After clicking <em>Open reset page</em>, choose a strong password (at least 10 characters, with uppercase, lowercase, a digit, and a special character). Then come back here for the next account.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="setup-token">Bootstrap token</Label>
                  <Input
                    id="setup-token"
                    type="password"
                    placeholder="Paste your BOOTSTRAP_ADMIN_TOKEN value"
                    value={bootstrapToken}
                    onChange={(e) => setBootstrapToken(e.target.value)}
                    autoComplete="off"
                    spellCheck={false}
                    required
                    data-testid="setup-token-input"
                  />
                  <p className="text-[11.5px] text-stone-500">
                    Same value you entered in Emergent deploy settings as <span className="font-mono">BOOTSTRAP_ADMIN_TOKEN</span>.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="setup-email">Account to reset</Label>
                  <select
                    id="setup-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#3F617E]"
                    data-testid="setup-email-select"
                  >
                    {allowlist.map((row) => (
                      <option key={row.email} value={row.email}>
                        {row.email} — {row.role}
                      </option>
                    ))}
                  </select>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[#3F617E] hover:bg-[#345470] rounded-full"
                  disabled={submitting}
                  data-testid="setup-submit-button"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Generate reset link
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-[11.5px] text-stone-400 mt-4">
          This page disappears (returns a calm 404 state) the moment you remove <span className="font-mono">BOOTSTRAP_ADMIN_TOKEN</span> from your env vars.
        </p>
      </div>
    </div>
  );
};

export default AdminSetup;
